import React, { useState } from 'react'
import { JobApplication } from './ApplicationWizard'
import { servicePlansService } from '../services/servicePlansService'
import {
  ACTION_OPERATION_KEYS,
  ActionType,
  CoachingContext,
  QualityTier,
} from '../services/communicationCoachService'
import './CommunicationCoachWizard.css'

interface CommunicationCoachWizardProps {
  application: JobApplication
  onComplete: (
    actionType: ActionType,
    input: string,
    context: CoachingContext,
    storeCommunication: boolean,
    qualityTier: QualityTier
  ) => void
  onCancel: () => void
}

type WizardStep = 'action' | 'input' | 'context' | 'storage' | 'processing'

const getDefaultRecruiterName = (application: JobApplication): string => {
  if (application.recruiter?.trim()) {
    return application.recruiter.trim()
  }

  const preferredContact = application.contacts?.find((contact) =>
    contact?.name && ['recruiter', 'hiring_manager', 'job_poster'].includes(contact.contactType)
  )

  return preferredContact?.name?.trim() || ''
}

const getDefaultContext = (application: JobApplication): CoachingContext => ({
  recruiter_name: getDefaultRecruiterName(application),
  company: application.company || '',
  position: application.position || '',
  communication_channel: 'email',
  goal: '',
  tone_preference: 'Professional and approachable',
  must_include: [],
  must_avoid: [],
  location: application.location || '',
  additional_notes: '',
})

const applyApplicationContextDefaults = (
  currentContext: CoachingContext,
  application: JobApplication,
): CoachingContext => {
  const defaultContext = getDefaultContext(application)
  const nextContext = {
    ...currentContext,
    recruiter_name: currentContext.recruiter_name?.trim() || defaultContext.recruiter_name,
    company: currentContext.company?.trim() || defaultContext.company,
    position: currentContext.position?.trim() || defaultContext.position,
    location: currentContext.location?.trim() || defaultContext.location,
  }

  if (
    nextContext.recruiter_name === currentContext.recruiter_name &&
    nextContext.company === currentContext.company &&
    nextContext.position === currentContext.position &&
    nextContext.location === currentContext.location
  ) {
    return currentContext
  }

  return nextContext
}

export const CommunicationCoachWizard: React.FC<CommunicationCoachWizardProps> = ({
  application,
  onComplete,
  onCancel
}) => {
  const [currentStep, setCurrentStep] = useState<WizardStep>('action')
  const [actionType, setActionType] = useState<ActionType | null>(null)
  const [input, setInput] = useState('')
  const [context, setContext] = useState<CoachingContext>(() => getDefaultContext(application))
  const [storeCommunication, setStoreCommunication] = useState(true)
  const [qualityTier, setQualityTier] = useState<QualityTier>('standard')
  const [mustIncludeText, setMustIncludeText] = useState('')
  const [mustAvoidText, setMustAvoidText] = useState('')
  const [tokenCosts, setTokenCosts] = useState<Record<string, number>>({})

  React.useEffect(() => {
    let cancelled = false

    const loadTokenCosts = async () => {
      try {
        const costs = await servicePlansService.getTokenCosts()
        if (cancelled) {
          return
        }

        const nextCosts = costs.reduce<Record<string, number>>((acc, item) => {
          acc[item.operationKey] = item.points
          return acc
        }, {})
        setTokenCosts(nextCosts)
      } catch (error) {
        console.error('Failed to load communication coach token costs:', error)
      }
    }

    loadTokenCosts()

    return () => {
      cancelled = true
    }
  }, [])

  const actionTypes: { value: ActionType; label: string; description: string }[] = [
    {
      value: 'email_response',
      label: 'Email Response',
      description: 'Generate a professional email response to recruiter communications'
    },
    {
      value: 'recruiter_outreach',
      label: 'Recruiter Outreach',
      description: 'Write a cold outreach email for this job using your selected or default resume plus the saved job post'
    },
    {
      value: 'cover_letter',
      label: 'Cover Letter',
      description: 'Generate a customized cover letter for this job using the saved job post and your selected or default resume'
    },
    {
      value: 'follow_up',
      label: 'Follow-Up',
      description: 'Draft a concise follow-up after applying, interviewing, or speaking with a recruiter'
    },
    {
      value: 'thank_you_note',
      label: 'Thank You Note',
      description: 'Write a polished thank-you note after an interview or recruiter conversation'
    },
    {
      value: 'interview_confirmation',
      label: 'Interview Confirmation',
      description: 'Confirm interview timing, availability, and logistics with clear professional wording'
    },
    {
      value: 'offer_response',
      label: 'Offer Response',
      description: 'Draft accept, clarify, request-time, or negotiate responses tied to this offer'
    },
    {
      value: 'message_review',
      label: 'Message Review',
      description: 'Review messages and emails for tone, clarity, and professionalism'
    },
    {
      value: 'writing_assistance',
      label: 'Writing Assistance',
      description: 'Get help improving your career communications'
    },
    {
      value: 'legal_review',
      label: 'Legal Review',
      description: 'Review job offers and contracts for red flags and negotiation points'
    },
    {
      value: 'salary_negotiation',
      label: 'Salary Negotiation',
      description: 'Get strategy and talking points for salary negotiations'
    }
  ]

  const parseListInput = (value: string) =>
    value
      .split(/\n|,/)
      .map((item) => item.trim())
      .filter(Boolean)

  const visibleActionTypes = actionTypes

  React.useEffect(() => {
    if (currentStep !== 'context') {
      return
    }

    setContext((current) => applyApplicationContextDefaults(current, application))
  }, [
    application.company,
    application.contacts,
    application.location,
    application.position,
    application.recruiter,
    currentStep,
  ])

  const selectedOperationKey = actionType ? ACTION_OPERATION_KEYS[actionType][qualityTier] : null
  const selectedTokenCost = selectedOperationKey ? tokenCosts[selectedOperationKey] : undefined

  const handleNext = () => {
    if (currentStep === 'action' && actionType) {
      setCurrentStep('input')
    } else if (currentStep === 'input' && (input.trim() || actionType === 'recruiter_outreach' || actionType === 'cover_letter')) {
      setContext((current) => applyApplicationContextDefaults(current, application))
      setCurrentStep('context')
    } else if (currentStep === 'context') {
      setCurrentStep('storage')
    } else if (currentStep === 'storage') {
      setCurrentStep('processing')
      if (actionType) {
        const normalizedInput = actionType === 'recruiter_outreach' || actionType === 'cover_letter'
          ? input.trim()
          : input
        onComplete(actionType, normalizedInput, context, storeCommunication, qualityTier)
      }
    }
  }

  const handleBack = () => {
    if (currentStep === 'input') {
      setCurrentStep('action')
    } else if (currentStep === 'context') {
      setCurrentStep('input')
    } else if (currentStep === 'storage') {
      setCurrentStep('context')
    }
  }

  const canProceed = () => {
    if (currentStep === 'action') return actionType !== null
    if (currentStep === 'input') return actionType === 'recruiter_outreach' || actionType === 'cover_letter' || input.trim().length > 0
    if (currentStep === 'context') return true
    if (currentStep === 'storage') return true
    return false
  }

  return (
    <div className="communication-coach-wizard">
      <div className="wizard-steps">
        <div className={`wizard-step ${currentStep === 'action' ? 'active' : 'completed'}`}>
          <span className="step-number">1</span>
          <span className="step-label">Action</span>
        </div>
        <div className={`wizard-step ${currentStep === 'input' ? 'active' : ['context', 'storage', 'processing'].includes(currentStep) ? 'completed' : ''}`}>
          <span className="step-number">2</span>
          <span className="step-label">Input</span>
        </div>
        <div className={`wizard-step ${currentStep === 'context' ? 'active' : ['storage', 'processing'].includes(currentStep) ? 'completed' : ''}`}>
          <span className="step-number">3</span>
          <span className="step-label">Context</span>
        </div>
        <div className={`wizard-step ${currentStep === 'storage' ? 'active' : currentStep === 'processing' ? 'completed' : ''}`}>
          <span className="step-number">4</span>
          <span className="step-label">Storage</span>
        </div>
      </div>

      <div className="wizard-content">
        {currentStep === 'action' && (
          <div className="wizard-step-content">
            <h3>Select Action Type</h3>
            <p>Choose the type of application-specific communication help you need:</p>
            <div className="wizard-tier-grid">
              <button
                className={`wizard-tier-card ${qualityTier === 'standard' ? 'selected' : ''}`}
                onClick={() => setQualityTier('standard')}
              >
                <h4>Standard</h4>
                <p>Uses the most cost-efficient model for everyday drafting and review.</p>
              </button>
              <button
                className={`wizard-tier-card ${qualityTier === 'premium' ? 'selected' : ''}`}
                onClick={() => setQualityTier('premium')}
              >
                <h4>Premium</h4>
                <p>Uses the stronger premium model for higher-stakes communication and tighter rewrites.</p>
              </button>
            </div>
            {selectedTokenCost !== undefined && (
              <div className="wizard-cost-summary">
                <strong>{qualityTier === 'premium' ? 'Premium' : 'Standard'} price:</strong> {selectedTokenCost} Kairos tokens
              </div>
            )}
            {qualityTier === 'premium' && (
              <div className="wizard-premium-note">
                Standard already uses strong models. Premium is available, but results may be similar and not noticeably better. Premium pricing reflects higher model costs.
              </div>
            )}
            <div className="action-type-grid">
              {visibleActionTypes.map((type) => (
                <button
                  key={type.value}
                  className={`action-type-card ${actionType === type.value ? 'selected' : ''}`}
                  onClick={() => setActionType(type.value)}
                >
                  <h4>{type.label}</h4>
                  <p>{type.description}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {currentStep === 'input' && (
          <div className="wizard-step-content">
            <h3>Enter Content</h3>
            <p>
              {actionType === 'email_response' && 'Paste the recruiter\'s email or message:'}
              {actionType === 'recruiter_outreach' && 'Optional: add a specific angle, recruiter name, or anything you want emphasized. Leave blank to use only the saved job post and selected/default resume:'}
              {actionType === 'cover_letter' && 'Optional: add specific points to emphasize, tone guidance, or a special angle. Leave blank to generate from the saved job post and selected/default resume:'}
              {actionType === 'follow_up' && 'Paste the latest thread or summarize where you are in the application process:'}
              {actionType === 'thank_you_note' && 'Paste your interview notes or summarize what you want to reference:'}
              {actionType === 'interview_confirmation' && 'Paste the scheduling message or the interview details you need to confirm:'}
              {actionType === 'offer_response' && 'Paste the offer email or summarize the offer terms and what response you need:'}
              {actionType === 'message_review' && 'Paste the message or email you want reviewed:'}
              {actionType === 'writing_assistance' && 'Enter the text you want to improve:'}
              {actionType === 'legal_review' && 'Paste the job offer or contract text:'}
              {actionType === 'salary_negotiation' && 'Enter the job offer details (salary, benefits, etc.):'}
            </p>
            <textarea
              className="wizard-textarea"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={actionType === 'recruiter_outreach' || actionType === 'cover_letter' ? 'Optional additional guidance...' : 'Paste or type your content here...'}
              rows={10}
            />
          </div>
        )}

        {currentStep === 'context' && (
          <div className="wizard-step-content">
            <h3>Additional Context</h3>
            <p>Provide additional context to help generate better results:</p>
            <div className="wizard-form-group">
              <label>Recruiter Name (optional)</label>
              <input
                type="text"
                value={context.recruiter_name || ''}
                onChange={(e) => setContext({ ...context, recruiter_name: e.target.value })}
                placeholder="Recruiter name"
              />
            </div>
            <div className="wizard-form-group">
              <label>Company</label>
              <input
                type="text"
                value={context.company || ''}
                onChange={(e) => setContext({ ...context, company: e.target.value })}
                placeholder="Company name"
              />
            </div>
            <div className="wizard-form-group">
              <label>Position</label>
              <input
                type="text"
                value={context.position || ''}
                onChange={(e) => setContext({ ...context, position: e.target.value })}
                placeholder="Job position"
              />
            </div>
            <div className="wizard-form-group">
              <label>Communication Channel</label>
              <input
                type="text"
                value={context.communication_channel || ''}
                onChange={(e) => setContext({ ...context, communication_channel: e.target.value })}
                placeholder="Email, LinkedIn, phone follow-up, etc."
              />
            </div>
            <div className="wizard-form-group">
              <label>Goal (optional)</label>
              <input
                type="text"
                value={context.goal || ''}
                onChange={(e) => setContext({ ...context, goal: e.target.value })}
                placeholder="Confirm interview details, keep momentum, ask for more time..."
              />
            </div>
            <div className="wizard-form-group">
              <label>Tone Preference (optional)</label>
              <input
                type="text"
                value={context.tone_preference || ''}
                onChange={(e) => setContext({ ...context, tone_preference: e.target.value })}
                placeholder="Warm, concise, confident, appreciative..."
              />
            </div>
            <div className="wizard-form-group">
              <label>Location (optional)</label>
              <input
                type="text"
                value={context.location || ''}
                onChange={(e) => setContext({ ...context, location: e.target.value })}
                placeholder="Location relevant to the application or offer"
              />
            </div>
            <div className="wizard-form-group">
              <label>Must Include (optional)</label>
              <textarea
                value={mustIncludeText}
                onChange={(e) => {
                  setMustIncludeText(e.target.value)
                  setContext({ ...context, must_include: parseListInput(e.target.value) })
                }}
                placeholder="One item per line or comma-separated"
                rows={3}
              />
            </div>
            <div className="wizard-form-group">
              <label>Must Avoid (optional)</label>
              <textarea
                value={mustAvoidText}
                onChange={(e) => {
                  setMustAvoidText(e.target.value)
                  setContext({ ...context, must_avoid: parseListInput(e.target.value) })
                }}
                placeholder="One item per line or comma-separated"
                rows={3}
              />
            </div>
            <div className="wizard-form-group">
              <label>Additional Notes (optional)</label>
              <textarea
                value={context.additional_notes || ''}
                onChange={(e) => setContext({ ...context, additional_notes: e.target.value })}
                placeholder="Any additional context or notes..."
                rows={4}
              />
            </div>
          </div>
        )}

        {currentStep === 'storage' && (
          <div className="wizard-step-content">
            <h3>Storage Option</h3>
            <p>Would you like to save this communication for future reference?</p>
            {selectedTokenCost !== undefined && (
              <p className="wizard-helper-text">
                This {qualityTier} request will use {selectedTokenCost} Kairos tokens.
              </p>
            )}
            <div className="wizard-checkbox-group">
              <label className="wizard-checkbox-label">
                <input
                  type="checkbox"
                  checked={storeCommunication}
                  onChange={(e) => setStoreCommunication(e.target.checked)}
                />
                <span>Store this communication</span>
              </label>
              <p className="wizard-checkbox-help">
                Save this communication to your account for future reference. You can view stored communications later.
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="wizard-footer">
        <button className="wizard-button wizard-button-secondary" onClick={currentStep === 'action' ? onCancel : handleBack}>
          {currentStep === 'action' ? 'Cancel' : 'Back'}
        </button>
        {currentStep !== 'storage' && (
          <button
            className="wizard-button wizard-button-primary"
            onClick={handleNext}
            disabled={!canProceed()}
          >
            Next
          </button>
        )}
        {currentStep === 'storage' && (
          <button
            className="wizard-button wizard-button-primary"
            onClick={handleNext}
          >
            Generate
          </button>
        )}
      </div>
    </div>
  )
}
